'use client'

import { usePathname } from 'next/navigation'
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, MessageCircle } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import { useOrderLookup } from '@/lib/commerce/hooks'

const STEPS = [
  { num: '01', title: 'Захиалгын дугаар оруулах', body: 'Мэйл болон захиалгын баталгаажуулалтын хуудаснаас олж болно.' },
  { num: '02', title: 'Утасны дугаар оруулах', body: 'Захиалга үүсгэхдээ ашигласан утасны дугаар.' },
  { num: '03', title: 'Захиалгын явц харах', body: 'Бэлдэж байгаа, хүргэгдсэн эсэхийг бодит цагт нь хянана.' },
] as const

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.13)',
  color: 'white',
}

export default function OrderCheck({ vendor, notFound, initialOrderNum, initialPhoneNum }: { vendor?: any, notFound?: boolean, initialOrderNum?: string, initialPhoneNum?: string }) {
  const pathname = usePathname()
  const supportEmail = vendor?.email || null
  const supportPhone = vendor?.phone || null
  const hasSupport = !!(supportEmail || supportPhone)
  const { orderNum, setOrderNum, phoneNum, setPhoneNum, errors, isLoading, submit } = useOrderLookup({ initialOrderNum, initialPhoneNum })

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-6">
        <a
          href={navigateUrl('/', pathname)}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:text-white"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Нүүр
        </a>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_460px] gap-12 lg:gap-16 items-start">
          <div className="min-w-0">
            <h1
              className="mt-3 font-semibold tracking-[-0.03em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(40px, 7vw, 72px)' }}
            >
              Захиалгаа<br />шалгах.
            </h1>
            <p className="mt-6 text-[15px] sm:text-[16px] leading-7 max-w-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Захиалга үүсгэсний дараа таны мэйл, утсанд илгээгдсэн{' '}
              <span className="text-white">захиалгын дугаар</span>,{' '}
              <span className="text-white">холбоо барих утсыг</span>{' '}
              оруулаад захиалгынхаа явцыг хянаарай.
            </p>

            <div className="mt-12 space-y-6">
              {STEPS.map(s => (
                <div key={s.num} className="flex items-start gap-5">
                  <span
                    className="text-[12px] font-mono tabular-nums tracking-[0.2em] shrink-0 mt-1"
                    style={{ color: 'rgba(255,255,255,0.4)' }}
                  >
                    {s.num}
                  </span>
                  <div>
                    <div className="text-[14px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                      {s.title}
                    </div>
                    <div className="mt-1 text-[13px] leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {s.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {hasSupport && (
              <div
                className="mt-12 rounded-2xl p-5 flex items-center gap-4"
                style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div
                  className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                >
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white font-medium">Захиалга олдохгүй байна уу?</div>
                  <div className="text-[12px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {supportEmail && supportPhone ? (
                      <>
                        <a href={`mailto:${supportEmail}`} className="text-white underline underline-offset-2 break-all">{supportEmail}</a>
                        {' '}руу бичих эсвэл{' '}
                        <a href={`tel:${supportPhone}`} className="text-white">{supportPhone}</a>
                        {' '}утсаар холбогдоно уу.
                      </>
                    ) : supportEmail ? (
                      <>
                        <a href={`mailto:${supportEmail}`} className="text-white underline underline-offset-2 break-all">{supportEmail}</a>
                        {' '}хаягаар бичнэ үү.
                      </>
                    ) : (
                      <>
                        <a href={`tel:${supportPhone}`} className="text-white">{supportPhone}</a>
                        {' '}утсаар холбогдоно уу.
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside
            className="rounded-2xl p-6 sm:p-7 lg:sticky lg:top-32 self-start"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              Захиалга шалгах
            </div>
            <h2
              className="mt-2 font-semibold tracking-[-0.015em] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(22px, 3vw, 26px)' }}
            >
              Мэдээллээ оруулаарай.
            </h2>

            {notFound && (
              <div className="mt-5 flex items-start gap-3 rounded-xl p-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#fca5a5' }} />
                <div className="text-[12px] leading-relaxed" style={{ color: '#fca5a5' }}>
                  Захиалга олдсонгүй. Дугаар, утсаа шалгаад дахин оролдоно уу.
                </div>
              </div>
            )}

            <div className="mt-7 space-y-4">
              <label className="block">
                <div className="text-[12px] font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Захиалгын дугаар <span style={{ color: '#fda4af' }}>*</span>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[13.5px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>#</span>
                  <input
                    value={orderNum}
                    onChange={(e) => setOrderNum(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="MN26123456"
                    className="w-full h-12 pl-8 pr-4 rounded-xl text-[13.5px] font-mono tabular-nums outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              </label>

              <label className="block">
                <div className="text-[12px] font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Утасны дугаар <span style={{ color: '#fda4af' }}>*</span>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[13.5px] tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>+976</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phoneNum}
                    onChange={(e) => setPhoneNum(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    onKeyDown={onKey}
                    autoComplete="tel"
                    placeholder="8000 0000"
                    className="w-full h-12 pl-16 pr-4 rounded-xl text-[13.5px] tabular-nums outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
                <div className="mt-2 text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Захиалга үүсгэхдээ ашигласан утасны дугаар.
                </div>
              </label>

              {(errors.orderNum || errors.phoneNum) && (
                <div className="text-[12px] font-medium" style={{ color: '#fca5a5' }}>
                  {errors.orderNum || errors.phoneNum}
                </div>
              )}

              <button
                onClick={submit}
                disabled={isLoading}
                className="w-full h-13 rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c', height: 52 }}
              >
                {isLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Уншиж байна</> : <>Захиалга шалгах <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            </div>

            <div className="mt-6 pt-6 text-[12px]" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' }}>
              <div className="flex items-center justify-between">
                <span>Бүртгэлтэй юу?</span>
                <a href={navigateUrl('/login', pathname)} className="text-white font-medium inline-flex items-center gap-1 hover:opacity-80 transition-opacity">
                  Нэвтрэх <ArrowRight className="h-3 w-3" />
                </a>
              </div>
              <div className="mt-2.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Хэрэв та бүртгэлээрээ захиалга хийсэн бол бүх захиалга бүртгэлд харагдана.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}
